// src/pages/UserManagement.jsx
import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  orderBy,
  query,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState("dark");
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const usersPerPage = 10;
  const navigate = useNavigate();

  // ✅ Auth protection with admin role check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/admin/login");
        return;
      }

      try {
        const q = query(collection(db, "adminUsers"), orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Case-insensitive email match
        const current = list.find(
          (u) =>
            u.email &&
            u.email.trim().toLowerCase() === user.email.trim().toLowerCase()
        );

        // 🔒 Allow only admins OR fallback super-admin email
        const superAdmin = "youremail@example.com"; // ✅ Replace with your email
        if (!current || current.role !== "admin") {
          if (user.email !== superAdmin) {
            alert("Access denied: Admins only.");
            await signOut(auth);
            navigate("/admin/login");
            return;
          }
        }
      } catch (err) {
        console.error(err);
        alert("Error verifying admin privileges.");
        await signOut(auth);
        navigate("/admin/login");
      }
    });

    return unsubscribe;
  }, [navigate]);

  // ✅ Load users from Firestore
  const loadUsers = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "adminUsers"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsers(list);
    } catch (err) {
      console.error(err);
      alert("Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // ✅ Add new admin user
  const addUser = async () => {
    if (!email) return alert("Enter an email.");
    try {
      await addDoc(collection(db, "adminUsers"), {
        email,
        role,
        createdAt: new Date(),
      });
      setEmail("");
      await loadUsers();
      alert("✅ Admin user added successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to add user.");
    }
  };

  // ✅ Change role (admin ↔ user)
  const toggleRole = async (id, currentRole) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    if (!window.confirm(`Change this user's role to ${newRole}?`)) return;
    try {
      await updateDoc(doc(db, "adminUsers", id), { role: newRole });
      setUsers((prev) =>
        prev.map((u) => (u.id === id ? { ...u, role: newRole } : u))
      );
    } catch (err) {
      console.error(err);
      alert("Failed to update role.");
    }
  };

  // ✅ Delete user
  const deleteUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await deleteDoc(doc(db, "adminUsers", id));
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      console.error(err);
      alert("Failed to delete user.");
    }
  };

  // ✅ Logout
  const handleLogout = async () => {
    await signOut(auth);
    navigate("/admin/login");
  };

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  // Pagination logic
  const filteredUsers = users.filter((user) => {
    const matchesSearch = !search || user.email?.toLowerCase().includes(search.toLowerCase());
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // Theme styles
  const containerClass =
    theme === "dark"
      ? "min-h-screen bg-[#071321] text-slate-100"
      : "min-h-screen bg-slate-100 text-slate-900";

  const sectionClass =
    theme === "dark"
      ? "rounded-2xl border border-slate-700/80 bg-slate-900/80 p-5 shadow-xl shadow-slate-950/20 sm:p-6"
      : "rounded-2xl border border-slate-200 bg-white p-5 shadow-lg shadow-slate-200/60 sm:p-6";

  const tableClass =
    theme === "dark"
      ? "w-full text-left text-sm text-slate-200"
      : "w-full text-left text-sm text-gray-800";

  return (
    <div className={containerClass}>
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-8 sm:py-9">
        <motion.header
          className={`mb-8 flex flex-wrap items-center justify-between gap-4 border-b pb-6 ${theme === "dark" ? "border-slate-700/60" : "border-slate-200"}`}
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-lg font-black text-white shadow-lg shadow-blue-950/40">
              C
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">Chidiz Wi-Fi · Admin workspace</p>
              <h1 className="mt-1 text-2xl font-extrabold sm:text-3xl">Team & access</h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate("/admin/dashboard")}
              className={`rounded-xl border px-4 py-2.5 text-sm font-semibold shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${theme === "dark" ? "border-slate-700 bg-slate-800/80 text-slate-200 hover:bg-slate-700 focus-visible:ring-offset-slate-900" : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 focus-visible:ring-offset-white"}`}
            >
              ← Dashboard
            </button>
            <button
              onClick={toggleTheme}
              className={`rounded-xl border px-4 py-2.5 text-sm font-semibold shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${theme === "dark" ? "border-slate-700 bg-slate-800/80 text-slate-200 hover:bg-slate-700 focus-visible:ring-offset-slate-900" : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 focus-visible:ring-offset-white"}`}
            >
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </button>
            <button
              onClick={handleLogout}
              className={`rounded-xl border px-4 py-2.5 text-sm font-semibold shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2 ${theme === "dark" ? "border-rose-500/20 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 focus-visible:ring-offset-slate-900" : "border-rose-200 bg-white text-rose-700 hover:border-rose-300 hover:bg-rose-50 focus-visible:ring-offset-white"}`}
            >
              Sign out
            </button>
          </div>
        </motion.header>

        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          {[
            { label: "Team members", value: users.length, note: "Accounts with workspace access", accent: "from-blue-500/20 to-cyan-500/5", color: "text-cyan-300" },
            { label: "Administrators", value: users.filter((user) => user.role === "admin").length, note: "Full management permissions", accent: "from-violet-500/20 to-fuchsia-500/5", color: "text-violet-300" },
            { label: "Standard users", value: users.filter((user) => user.role !== "admin").length, note: "Restricted workspace access", accent: "from-emerald-500/20 to-teal-500/5", color: "text-emerald-300" },
          ].map((stat) => (
            <motion.div
              key={stat.label}
              className={`rounded-2xl border p-5 ${theme === "dark" ? "border-slate-700/80 bg-gradient-to-br " + stat.accent + " to-slate-900" : "border-slate-200 bg-white shadow-lg shadow-slate-200/50"}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className={`text-sm font-semibold ${theme === "dark" ? stat.color : "text-blue-700"}`}>{stat.label}</p>
              <p className="mt-3 text-3xl font-extrabold">{stat.value}</p>
              <p className={`mt-1 text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{stat.note}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.7fr)]">
          <motion.section className={sectionClass} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mb-5 flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/15 text-blue-300">
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </span>
              <div>
                <h2 className="text-lg font-bold">Invite a teammate</h2>
                <p className={`mt-1 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>Add an email and choose its access level.</p>
              </div>
            </div>
            <label className={`mb-2 block text-xs font-semibold uppercase tracking-wider ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`} htmlFor="team-email">
              Email address
            </label>
            <input
              id="team-email"
              type="email"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mb-4 w-full rounded-xl border p-3.5 outline-none transition focus:ring-2 ${
                theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20" : "border-slate-300 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500/20"
              }`}
            />
            <label className={`mb-2 block text-xs font-semibold uppercase tracking-wider ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`} htmlFor="team-role">
              Access role
            </label>
            <select
              id="team-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className={`mb-5 w-full rounded-xl border p-3.5 outline-none focus:ring-2 ${
                theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white focus:ring-blue-500/30" : "border-slate-300 bg-white text-slate-900 focus:ring-blue-500/20"
              }`}
            >
              <option value="admin">Administrator · Full access</option>
              <option value="user">Standard user · Restricted access</option>
            </select>
            <button
              onClick={addUser}
              className="w-full rounded-xl bg-blue-700 px-5 py-3 font-bold text-white shadow-lg shadow-blue-900/20 transition hover:bg-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white active:translate-y-px"
            >
              Add team member
            </button>
            <p className={`mt-4 text-xs leading-relaxed ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>
              Assign administrator access only to trusted team members.
            </p>
          </motion.section>

          <motion.section className={`${sectionClass} min-w-0`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold">Team directory</h2>
                <p className={`mt-1 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{filteredUsers.length} matching {filteredUsers.length === 1 ? "member" : "members"}</p>
              </div>
              <button
                onClick={loadUsers}
                disabled={loading}
                className={`rounded-xl border px-4 py-2.5 text-sm font-semibold shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${theme === "dark" ? "border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 focus-visible:ring-offset-slate-900" : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 focus-visible:ring-offset-white"}`}
              >
                {loading ? "Refreshing…" : "Refresh list"}
              </button>
            </div>

            <div className="mb-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_190px]">
              <label className="relative block">
                <span className="sr-only">Search by email</span>
                <svg viewBox="0 0 24 24" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="10.8" cy="10.8" r="6.8" />
                  <path d="m16 16 4.5 4.5" />
                </svg>
                <input
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search team by email"
                  className={`w-full rounded-xl border py-2.5 pl-9 pr-3 text-sm outline-none focus:ring-2 ${theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white placeholder:text-slate-500 focus:ring-blue-500/30" : "border-slate-300 bg-white text-slate-900 focus:ring-blue-500/20"}`}
                />
              </label>
              <select
                value={roleFilter}
                onChange={(event) => {
                  setRoleFilter(event.target.value);
                  setCurrentPage(1);
                }}
                aria-label="Filter team by role"
                className={`rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 ${theme === "dark" ? "border-slate-700 bg-slate-950/70 text-white focus:ring-blue-500/30" : "border-slate-300 bg-white text-slate-900 focus:ring-blue-500/20"}`}
              >
                <option value="all">All roles</option>
                <option value="admin">Administrators</option>
                <option value="user">Standard users</option>
              </select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center gap-3 py-16 text-sm text-slate-400">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-blue-400 border-t-transparent" />
                Loading team directory…
              </div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-xl border border-slate-700/70">
                  <table className={tableClass}>
                    <thead className={theme === "dark" ? "bg-slate-950/70 text-slate-400" : "bg-slate-50 text-slate-500"}>
                      <tr className="text-left text-[11px] uppercase tracking-wider">
                        <th className="px-4 py-3">Member</th>
                        <th className="px-4 py-3">Role</th>
                        <th className="px-4 py-3">Added</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentUsers.map((u) => (
                        <tr key={u.id} className={`border-t ${theme === "dark" ? "border-slate-700/70 hover:bg-white/[0.025]" : "border-slate-200 hover:bg-slate-50"}`}>
                          <td className="px-4 py-3.5">
                            <div className="flex min-w-[180px] items-center gap-3">
                              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-500/15 text-xs font-bold uppercase text-blue-300">
                                {(u.email || "?").slice(0, 2)}
                              </span>
                              <span className="truncate font-medium">{u.email}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${u.role === "admin" ? "bg-violet-500/15 text-violet-300" : "bg-slate-500/15 text-slate-400"}`}>
                              {u.role === "admin" ? "Administrator" : "Standard user"}
                            </span>
                          </td>
                          <td className={`whitespace-nowrap px-4 py-3.5 text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                            {u.createdAt?.seconds ? new Date(u.createdAt.seconds * 1000).toLocaleDateString() : "-"}
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => toggleRole(u.id, u.role)}
                                className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1 ${theme === "dark" ? "border-amber-500/20 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20" : "border-amber-300 bg-amber-50 text-amber-800 hover:border-amber-400 hover:bg-amber-100"}`}
                              >
                                Change role
                              </button>
                              <button
                                onClick={() => deleteUser(u.id)}
                                className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-1 ${theme === "dark" ? "border-rose-500/20 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20" : "border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-300 hover:bg-rose-100"}`}
                              >
                                Remove
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {currentUsers.length === 0 && (
                        <tr>
                          <td colSpan="4" className="px-4 py-12 text-center text-sm text-slate-400">
                            No team members match these filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 flex items-center justify-between gap-4">
                  <p className={`text-xs ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>
                    Showing {filteredUsers.length ? indexOfFirstUser + 1 : 0}–{Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={prevPage}
                      disabled={currentPage === 1}
                      className={`rounded-lg border px-3 py-2 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40 ${theme === "dark" ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"}`}
                    >
                      Previous
                    </button>
                    <span className={`min-w-16 text-center text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>Page {currentPage} / {totalPages || 1}</span>
                    <button
                      onClick={nextPage}
                      disabled={currentPage === totalPages || totalPages === 0}
                    className={`rounded-lg border px-3 py-2 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40 ${theme === "dark" ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"}`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.section>
        </div>
      </div>
    </div>
  );
}
